/**
 * wysiwyg.js - Replace HTML textareas with WYSIWYG editor.
 */
import parseOptions from './parse_options.js';
import FindAndReplace from '@ckeditor/ckeditor5-find-and-replace/src/findandreplace';
import StudipBlockQuote from '../cke/studip-quote/StudipBlockQuote.js';
import WikiLink from '../cke/wiki-link/wiki-link.js';

const wysiwyg = {
    // NOTE keep this function in sync with Markup class
    htmlMarker: '<!--HTML-->',
    htmlMarkerRegExp: /^\s*<!--\s*HTML.*?-->/i,

    isHtml: function isHtml(text) {
        // NOTE keep this function in sync with
        // Markup::isHtml in Markup.class.php
        return this.hasHtmlMarker(text);
    },
    hasHtmlMarker: function hasHtmlMarker(text) {
        // NOTE keep this function in sync with
        // Markup::hasHtmlMarker in Markup.class.php
        return this.htmlMarkerRegExp.test(text);
    },
    markAsHtml: function markAsHtml(text) {
        // NOTE keep this function in sync with
        // Markup::markAsHtml in Markup.class.php
        if (this.hasHtmlMarker(text) || text.trim() == '') {
            return text; // marker already set, don't set twice
        }
        return this.htmlMarker + text;
    },

    getEditor,
    hasEditor,

    replace: replaceTextarea,
};

export default wysiwyg;

function replaceTextarea(textarea) {
    if (hasEditor(textarea)) {
        return getEditor(textarea);
    }

    setEditor(textarea, {});
    const $textarea = textarea instanceof jQuery ? textarea : $(textarea);

    let options = {};

    if ($textarea.attr('data-editor')) {
        const parsed = parseOptions($textarea.attr('data-editor'));

        if (parsed.toolbar === 'small') {
            options.toolbar = {
                removeItems: [
                    'undo',
                    'redo',
                    'strikethrough',
                    'horizontalLine',
                ]
            };
        } else if (parsed.toolbar === 'minimal') {
            options.toolbar = {
                items: [
                    'bold',
                    'italic',
                    'underline',
                    'subscript',
                    'superscript',
                    'fontColor',
                    'fontBackgroundColor',
                    '|',
                    'removeFormat',
                    '|',
                    'bulletedList',
                    'numberedList',
                    '|',
                    'link',
                    'math',
                    'specialCharacters',
                ]
            };
        }

        if (parsed.removePlugins) {
            options.removePlugins = parsed.removePlugins.split(",")
        }

        if (parsed.extraPlugins) {
            const pluginMap = { FindAndReplace, StudipBlockQuote, WikiLink };
            options.extraPlugins = parsed.extraPlugins.split(",").reduce((memo, plugin) => {
                if (plugin in pluginMap) {
                    memo.push(pluginMap[plugin]);
                }
                return memo;
            }, []);
        }
    }

    return STUDIP.loadChunk('wysiwyg')
        .then(loadMathJax)
        .then(createEditor)
        .then(setEditorInstance)
        .then(enhanceEditor)
        .then(emitLoadEvent);

    function createEditor(ClassicEditor) {
        return ClassicEditor.create(textarea, options).then(editor => {
            function getViewportOffsetTop() {
                const topBar = document.getElementById('top-bar');
                const responsiveContentbar = document.getElementById('responsive-contentbar');

                let top = topBar.clientHeight + topBar.clientTop;
                if (responsiveContentbar) {
                    top += responsiveContentbar?.clientHeight + responsiveContentbar.clientTop;
                }

                return top;
            }

            function updateOffsetTop() {
                // This needs to be delayed since some events will fire before
                // changing the DOM
                setTimeout(() => {
                    editor.ui.viewportOffset = {top: getViewportOffsetTop()};
                    editor.ui.update();
                }, 50);
            }

            // Set initial offset top
            updateOffsetTop();

            // Listen to relevant events that may require the sticky panel to be misplaced
            STUDIP.eventBus.on('toggle-compact-navigation', updateOffsetTop);
            STUDIP.eventBus.on('switch-focus-mode', updateOffsetTop);

            // Stop listening if editor is destroyed
            editor.on('destroy', () => {
                STUDIP.eventBus.off('toggle-compact-navigation', updateOffsetTop);
                STUDIP.eventBus.off('switch-focus-mode', updateOffsetTop);
            });

            return editor;
        });
    }

    function setEditorInstance(ckeditor) {
        setEditor(textarea, ckeditor);
        return ckeditor;
    }

    function enhanceEditor(ckeditor) {
        // make sure HTML marker is always set, in
        // case contents are cut-off by the backend
        $textarea.closest('form').submit(() => {
            // only trigger if the editor is still attached to the textarea
            if (getEditor($textarea[0]) === ckeditor) {
                ckeditor.setData(wysiwyg.markAsHtml(ckeditor.getData()));
                ckeditor.updateSourceElement();
            }
        });

        // focus the editor if requested
        if ($textarea.is('[autofocus]')) {
            ckeditor.focus();
        }

        ckeditor.ui.focusTracker.on('change:isFocused', (evt, name, isFocused) => {
            if (!isFocused) {
                ckeditor.sourceElement.value = wysiwyg.markAsHtml(ckeditor.getData());
            }
        });

        const button = ckeditor.ui.view.toolbar.items.find( item => item.class === "ck-source-editing-button");
        if (button) {
            button.withText = false;
        }

        // Tell MathJax v2.7 to leave the editor alone
        ckeditor.ui.element.classList.add('tex2jax_ignore');

        return ckeditor;
    }

    function emitLoadEvent(ckeditor) {
        $textarea.trigger('load.wysiwyg');

        return ckeditor;
    }

    async function loadMathJax(ckeditor) {
        let mathjaxP;

        if (window.MathJax && window.MathJax.Hub) {
            mathjaxP = Promise.resolve(window.MathJax);
        } else if (window.STUDIP && window.STUDIP.loadChunk) {
            mathjaxP = window.STUDIP.loadChunk('mathjax');
        }

        await mathjaxP;

        //console.log('loading MathJaxP...', mathjaxP);
        return ckeditor;
    }
}

const instances = new Map();
let internalIdCounter = 0;

function getEditor(textarea) {
    return textarea.dataset.editorId !== undefined ? instances.get(textarea.dataset.editorId) : null;
}

function hasEditor(textarea) {
    return textarea.dataset.editorId !== undefined && instances.has(textarea.dataset.editorId);
}

function setEditor(textarea, editor) {
    if (textarea.dataset.editorId === undefined) {
        textarea.dataset.editorId = String(internalIdCounter++);
    }
    instances.set(textarea.dataset.editorId, editor);
}

function unsetEditor(textarea) {
    instances.delete(textarea.dataset.editorId);
}
